/**
 * Views/HouseTreasury/api.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Thin wrappers over the House Treasury cloud functions.
 *
 * The module is read-only by design (requirement §7 / AC-03): there is no Add
 * VC, Redeem VC or Transfer VC anywhere in this tree. The only writes are
 * freeze, unfreeze and reconciliation exception resolution — none of which
 * move VC.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { Parse } from "parse";

async function run(fn, params = {}) {
  try {
    return await Parse.Cloud.run(fn, params);
  } catch (err) {
    // Parse wraps cloud errors; surface the message the backend actually sent
    // rather than a generic "Cloud Code returned an error".
    const message = err?.message || err?.error || "Request failed.";
    const wrapped = new Error(message);
    wrapped.code = err?.code;
    throw wrapped;
  }
}

// ── Shared ───────────────────────────────────────────────────────────────────
export const getTreasuryStatus = () => run("getTreasuryStatus");
export const getGameOwnerOptions = () => run("getTreasuryGameOwnerOptions");
export const syncWallets = () => run("syncTreasuryWallets");

// ── Tab 1: Dashboard ─────────────────────────────────────────────────────────
export const getDashboard = (params) => run("getTreasuryDashboard", params);

// ── Tab 2: Game Owner Performance ────────────────────────────────────────────
export const getGameOwners = (params) => run("getTreasuryGameOwners", params);
export const getHierarchyNode = (params) => run("getTreasuryHierarchyNode", params);
export const getAccountTransactions = (params) =>
  run("getTreasuryAccountTransactions", params);

// ── Tab 3: Games ─────────────────────────────────────────────────────────────
export const getGames = (params) => run("getTreasuryGames", params);
export const getGameDetail = (params) => run("getTreasuryGameDetail", params);
export const getGameRounds = (params) => run("getTreasuryGameRounds", params);

// ── Tab 4: Treasury Controls ─────────────────────────────────────────────────
export const getControls = () => run("getTreasuryControls");
export const freezeTreasury = (reason) => run("freezeTreasury", { reason });
export const unfreezeTreasury = (reason) => run("unfreezeTreasury", { reason });

// ── Tab 5: Complete Ledger ───────────────────────────────────────────────────
export const getLedger = (params) => run("getTreasuryLedger", params);
export const getLedgerFilterOptions = () => run("getTreasuryLedgerFilterOptions");
export const getLedgerDetail = (params) => run("getTreasuryLedgerDetail", params);
export const traceTransaction = (params) => run("traceTreasuryTransaction", params);
export const exportLedger = (params) => run("exportTreasuryLedger", params);

// ── Tab 6: Daily Report ──────────────────────────────────────────────────────
export const getReports = (params) => run("getTreasuryReports", params);
export const getReportDetail = (params) => run("getTreasuryReportDetail", params);
export const generateReport = (params) => run("generateTreasuryDailyReport", params);
export const retryReportEmail = (reportId) =>
  run("retryTreasuryReportEmail", { reportId });

// ── Tab 7: Audit Logs ────────────────────────────────────────────────────────
export const getAuditLogs = (params) => run("getTreasuryAuditLogs", params);
export const getAuditDetail = (params) => run("getTreasuryAuditDetail", params);

// ── Reconciliation ───────────────────────────────────────────────────────────
export const getReconciliation = (params) => run("getTreasuryReconciliation", params);
export const resolveException = (params) => run("resolveTreasuryException", params);
